import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  HStack,
  Heading,
  IconButton,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  OrderedList,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  FaArrowRight,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaLink,
} from 'react-icons/fa';

import { submitLandingForm } from '../services/landingService';
import { getRateLimitService } from '../services/rateLimitService';
import { logger } from '../utils/logger';

const PRIMARY = '#0066FF';
const INK = '#0A1F44';
const BAR_BG = 'rgba(186, 217, 255, 0.45)';
const BAR_BORDER = 'rgba(120, 170, 230, 0.55)';
const MUTED_INK = 'rgba(10, 31, 68, 0.7)';

const rateLimitService = getRateLimitService();

const AgencyFloatingProspectBar = () => {
  const [stage, setStage] = useState('url');
  const [clientUrl, setClientUrl] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalTarget(document.body);
    }
  }, []);

  const handleUrlSubmit = (e) => {
    e.preventDefault();
    const trimmed = clientUrl.trim();
    if (!trimmed) {
      setError('Enter a potential client URL (e.g. competitor.com)');
      return;
    }
    setError('');
    setStage('details');
  };

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    if (rateLimitService.isRateLimited('agencyHeroSubmit')) {
      setError('Please wait a moment before trying again.');
      return;
    }
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!email || !email.includes('@')) {
      setError('Please enter a valid work email');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await submitLandingForm(
        email,
        `Agency Lead · URL: ${clientUrl.trim()}${agencyName.trim() ? ` · Agency: ${agencyName.trim()}` : ''}`,
        name.trim(),
        agencyName.trim() || undefined,
      );
      setStage('success');
    } catch (err) {
      logger.error('Agency floating bar submission failed', err);
      setError(err?.message || 'Unable to submit right now. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setStage('url');
    setClientUrl('');
    setEmail('');
    setName('');
    setAgencyName('');
    setError('');
  };

  const bar = (
    <Box
      id="agency-signup-form"
      position="fixed"
      bottom={{ base: 2, md: 4 }}
      left={{ base: 2, md: 4 }}
      right={{ base: 2, md: 4 }}
      zIndex={1100}
      pointerEvents="auto"
      data-testid="agency-floating-prospect-bar"
      sx={{ position: 'fixed' }}
    >
      <Box
        maxW="1080px"
        mx="auto"
        bg={BAR_BG}
        borderRadius={{ base: 'lg', md: 'xl' }}
        boxShadow="0 16px 40px -16px rgba(10,31,68,0.25), 0 4px 12px -6px rgba(10,31,68,0.18)"
        border="1px solid"
        borderColor={BAR_BORDER}
        backdropFilter="blur(14px) saturate(140%)"
        sx={{ WebkitBackdropFilter: 'blur(14px) saturate(140%)' }}
        px={{ base: 2.5, md: 4 }}
        py={{ base: collapsed ? 1.5 : 2.5, md: collapsed ? 1.5 : 3 }}
        position="relative"
        overflow="hidden"
      >
        <IconButton
          aria-label={collapsed ? 'Expand prospect bar' : 'Minimize prospect bar'}
          icon={<Icon as={collapsed ? FaChevronUp : FaChevronDown} boxSize={2.5} />}
          size="xs"
          variant="ghost"
          color={MUTED_INK}
          position="absolute"
          top={1}
          right={1}
          zIndex={2}
          minW="20px"
          h="20px"
          _hover={{ bg: 'rgba(10,31,68,0.08)', color: INK }}
          onClick={() => setCollapsed((c) => !c)}
          data-testid="agency-floating-prospect-bar-toggle"
        />

        {collapsed ? (
          <HStack
            spacing={3}
            align="center"
            justify="space-between"
            position="relative"
            zIndex={1}
            pr={6}
          >
            <Text color={INK} fontWeight="700" fontSize="sm">
              Run this on your prospect
            </Text>
            <Button
              size="xs"
              bg={PRIMARY}
              color="white"
              borderRadius="md"
              fontWeight="700"
              rightIcon={<Icon as={FaArrowRight} boxSize={2.5} />}
              _hover={{ bg: '#0052CC' }}
              _active={{ bg: '#0052CC' }}
              onClick={() => setCollapsed(false)}
            >
              Open
            </Button>
          </HStack>
        ) : (
          <Flex
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'stretch', md: 'center' }}
            gap={{ base: 2, md: 4 }}
            position="relative"
            zIndex={1}
            pr={{ base: 0, md: 6 }}
          >
            <VStack
              align={{ base: 'center', md: 'flex-start' }}
              spacing={0.5}
              textAlign={{ base: 'center', md: 'left' }}
              flexShrink={0}
              maxW={{ base: 'full', md: '240px' }}
            >
              <Heading
                as="h3"
                fontSize={{ base: 'sm', md: 'md' }}
                color={INK}
                fontWeight="800"
                letterSpacing="-0.01em"
                lineHeight="1.15"
              >
                {stage === 'success'
                  ? 'Check your inbox.'
                  : 'Run this on your prospect.'}
              </Heading>
              <Text fontSize="11px" color={MUTED_INK} lineHeight="1.4" display={{ base: 'none', md: 'block' }}>
                {stage === 'success'
                  ? "We'll send a whitelabeled preview within 24 hours."
                  : 'Drop a URL — we build the exact same dashboard above for them, whitelabeled.'}
              </Text>
            </VStack>

            <Box flex="1" w="full">
              {stage === 'url' && (
                <form onSubmit={handleUrlSubmit}>
                  <VStack align="stretch" spacing={1.5}>
                    <Flex direction={{ base: 'column', md: 'row' }} gap={2} w="full">
                      <InputGroup size="sm" flex="1">
                        <InputLeftElement pointerEvents="none" h="full">
                          <Flex w="16px" h="16px" justify="center" align="center" flexShrink={0}>
                            <Icon as={FaLink} color={MUTED_INK} boxSize={3} />
                          </Flex>
                        </InputLeftElement>
                        <Input
                          placeholder="Potential Client URL (e.g. competitor.com)"
                          value={clientUrl}
                          onChange={(e) => {
                            setClientUrl(e.target.value);
                            if (error) setError('');
                          }}
                          bg="whiteAlpha.700"
                          color={INK}
                          border="1px solid"
                          borderColor="rgba(10,31,68,0.18)"
                          borderRadius="md"
                          _placeholder={{ color: 'rgba(10,31,68,0.45)' }}
                          _hover={{ borderColor: 'rgba(10,31,68,0.32)' }}
                          _focus={{
                            borderColor: PRIMARY,
                            boxShadow: `0 0 0 1px ${PRIMARY}`,
                            bg: 'whiteAlpha.900',
                          }}
                          autoComplete="url"
                          data-testid="agency-floating-url-input"
                        />
                      </InputGroup>
                      <Button
                        type="submit"
                        size="sm"
                        bg={PRIMARY}
                        color="white"
                        borderRadius="md"
                        fontWeight="700"
                        px={4}
                        rightIcon={<Icon as={FaArrowRight} boxSize={3} />}
                        _hover={{ bg: '#0052CC', transform: 'translateY(-1px)' }}
                        _active={{ bg: '#0052CC' }}
                        boxShadow="0 6px 16px -6px rgba(0,102,255,0.5)"
                        data-testid="agency-floating-url-submit"
                      >
                        Get My Report
                      </Button>
                    </Flex>
                    {error && (
                      <Text color="red.600" fontSize="xs" fontWeight="500">
                        {error}
                      </Text>
                    )}
                    <HStack
                      spacing={4}
                      flexWrap="wrap"
                      justify={{ base: 'center', md: 'flex-start' }}
                      align="center"
                      display={{ base: 'none', md: 'flex' }}
                    >
                      {['No APIs required', 'Whitelabeled', 'Instant setup'].map((item) => (
                        <HStack key={item} spacing={1.5} align="center">
                          <Flex w="14px" h="14px" justify="center" align="center" flexShrink={0}>
                            <Icon as={FaCheckCircle} color={PRIMARY} boxSize={3} />
                          </Flex>
                          <Text mb={0} fontSize="11px" color={MUTED_INK} fontWeight="500" lineHeight="1">
                            {item}
                          </Text>
                        </HStack>
                      ))}
                    </HStack>
                  </VStack>
                </form>
              )}

              {stage === 'details' && (
                <form onSubmit={handleDetailsSubmit}>
                  <VStack align="stretch" spacing={2}>
                    <HStack
                      spacing={2}
                      color={MUTED_INK}
                      fontSize="xs"
                      bg="whiteAlpha.600"
                      border="1px solid"
                      borderColor="rgba(10,31,68,0.15)"
                      borderRadius="md"
                      px={3}
                      py={1.5}
                      align="center"
                    >
                      <Flex w="14px" h="14px" justify="center" align="center" flexShrink={0}>
                        <Icon as={FaLink} boxSize={3} color={PRIMARY} />
                      </Flex>
                      <Text noOfLines={1} flex="1">
                        Analyzing{' '}
                        <Text as="span" fontWeight="700" color={INK}>
                          {clientUrl}
                        </Text>
                      </Text>
                      <Button
                        variant="link"
                        size="xs"
                        color={MUTED_INK}
                        fontWeight="600"
                        onClick={() => setStage('url')}
                        _hover={{ color: INK, textDecoration: 'none' }}
                      >
                        Change
                      </Button>
                    </HStack>
                    <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
                      <FormControl isRequired flex="1">
                        <Input
                          placeholder="Your name"
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                            if (error) setError('');
                          }}
                          size="sm"
                          bg="whiteAlpha.700"
                          color={INK}
                          border="1px solid"
                          borderColor="rgba(10,31,68,0.18)"
                          borderRadius="md"
                          _placeholder={{ color: 'rgba(10,31,68,0.45)' }}
                          _hover={{ borderColor: 'rgba(10,31,68,0.32)' }}
                          _focus={{
                            borderColor: PRIMARY,
                            boxShadow: `0 0 0 1px ${PRIMARY}`,
                            bg: 'whiteAlpha.900',
                          }}
                          data-testid="agency-floating-name-input"
                        />
                      </FormControl>
                      <FormControl isRequired flex="1">
                        <Input
                          placeholder="Work email"
                          type="email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (error) setError('');
                          }}
                          size="sm"
                          bg="whiteAlpha.700"
                          color={INK}
                          border="1px solid"
                          borderColor="rgba(10,31,68,0.18)"
                          borderRadius="md"
                          _placeholder={{ color: 'rgba(10,31,68,0.45)' }}
                          _hover={{ borderColor: 'rgba(10,31,68,0.32)' }}
                          _focus={{
                            borderColor: PRIMARY,
                            boxShadow: `0 0 0 1px ${PRIMARY}`,
                            bg: 'whiteAlpha.900',
                          }}
                          data-testid="agency-floating-email-input"
                        />
                      </FormControl>
                      <FormControl flex="1">
                        <Input
                          placeholder="Agency name (optional)"
                          value={agencyName}
                          onChange={(e) => setAgencyName(e.target.value)}
                          size="sm"
                          bg="whiteAlpha.700"
                          color={INK}
                          border="1px solid"
                          borderColor="rgba(10,31,68,0.18)"
                          borderRadius="md"
                          _placeholder={{ color: 'rgba(10,31,68,0.45)' }}
                          _hover={{ borderColor: 'rgba(10,31,68,0.32)' }}
                          _focus={{
                            borderColor: PRIMARY,
                            boxShadow: `0 0 0 1px ${PRIMARY}`,
                            bg: 'whiteAlpha.900',
                          }}
                          data-testid="agency-floating-agency-input"
                        />
                      </FormControl>
                      <Button
                        type="submit"
                        size="sm"
                        bg={PRIMARY}
                        color="white"
                        borderRadius="md"
                        fontWeight="700"
                        px={4}
                        rightIcon={isLoading ? <Spinner size="sm" /> : <Icon as={FaArrowRight} boxSize={3} />}
                        isDisabled={isLoading}
                        _hover={{ bg: '#0052CC', transform: 'translateY(-1px)' }}
                        _active={{ bg: '#0052CC' }}
                        boxShadow="0 6px 16px -6px rgba(0,102,255,0.5)"
                        data-testid="agency-floating-details-submit"
                      >
                        {isLoading ? 'Sending…' : 'Send My Report'}
                      </Button>
                    </Flex>
                    {error && (
                      <Text color="red.600" fontSize="xs" fontWeight="500">
                        {error}
                      </Text>
                    )}
                  </VStack>
                </form>
              )}

              {stage === 'success' && (
                <VStack align="stretch" spacing={2}>
                  <HStack
                    spacing={3}
                    bg="whiteAlpha.600"
                    border="1px solid"
                    borderColor="rgba(10,31,68,0.15)"
                    borderRadius="md"
                    px={3}
                    py={2}
                    align="center"
                  >
                    <Flex
                      w="28px"
                      h="28px"
                      bg="green.500"
                      borderRadius="full"
                      align="center"
                      justify="center"
                      flexShrink={0}
                    >
                      <Icon as={FaCheckCircle} color="white" boxSize={3} />
                    </Flex>
                    <VStack align="flex-start" spacing={0} flex="1">
                      <Text fontWeight="700" color={INK} fontSize="sm">
                        You&apos;re in, {name.split(' ')[0] || 'thanks'}.
                      </Text>
                      <Text fontSize="xs" color={MUTED_INK} noOfLines={1}>
                        We&apos;re building a whitelabeled preview of{' '}
                        <Text as="span" color={INK} fontWeight="600">
                          {clientUrl}
                        </Text>
                        .
                      </Text>
                    </VStack>
                    <Button
                      onClick={resetForm}
                      size="xs"
                      variant="outline"
                      color={INK}
                      borderColor="rgba(10,31,68,0.3)"
                      borderRadius="md"
                      fontWeight="600"
                      _hover={{ bg: 'whiteAlpha.700', borderColor: 'rgba(10,31,68,0.5)' }}
                    >
                      Submit another
                    </Button>
                  </HStack>
                  <Box display={{ base: 'none', md: 'block' }}>
                    <OrderedList spacing={0.5} pl={4} color={MUTED_INK} fontSize="11px">
                      <ListItem>We pull public review, traffic, and SEO data for the URL</ListItem>
                      <ListItem>You receive a whitelabeled preview within 24 hours</ListItem>
                    </OrderedList>
                  </Box>
                </VStack>
              )}
            </Box>
          </Flex>
        )}
      </Box>
    </Box>
  );

  if (!portalTarget) {
    return null;
  }

  return createPortal(bar, portalTarget);
};

export default AgencyFloatingProspectBar;
